import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, Label,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const PerformanceTechnicalPage = () => {
  const [offlineData, setOfflineData] = useState(null);
  const [crashData, setCrashData] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [notificationData, setNotificationData] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [offline, crash, api, notification, version] = await Promise.all([
        axios.get(`${API}/super-admin/app-analytics/offline-usage`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/crash-rate`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/api-response-times`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/notification-open-rate`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/super-admin/app-analytics/app-versions`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setOfflineData(offline.data);
      setCrashData(crash.data);
      setApiData(api.data);
      setNotificationData(notification.data);
      setVersionData(version.data);
    } catch (err) {
      console.error('Failed to fetch performance data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading performance analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Performance & Technical Metrics</h1>
          <p className="text-purple-200 text-sm">Mobile app performance, stability, and technical analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchAll} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Performance & Technical Metrics</h1>
              <p className="text-purple-200 text-sm">Mobile app performance, stability, and technical analytics</p>
            </div>
          </div>
          <button onClick={fetchAll} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Chart 1: Offline Mode Usage (FUNCTIONAL) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Offline Mode Usage (Last 30 Days)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={offlineData?.daily_data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis>
              <Label value="Clock-Ins" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
            </YAxis>
            <Tooltip />
            <Legend />
            <Bar dataKey="offline_count" stackId="a" fill="#f59e0b" name="Offline" />
            <Bar dataKey="online_count" stackId="a" fill="#10b981" name="Online" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-gray-700"><strong>What this shows:</strong> Daily breakdown of clock-ins performed offline (amber) vs online (green) showing connectivity patterns.</p>
          <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> High offline usage indicates poor connectivity at work sites or robust offline feature adoption. If offline percentage is high (greater than 30%), ensure offline sync is reliable and prioritize offline-first features. Low offline usage with connectivity issues suggests users struggling or feature not working. Use this to plan offline feature investments and understand work site connectivity challenges.</p>
        </div>
      </div>

      {/* Chart 2: App Crash Rate */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">App Crash Rate</h3>
          <p className="text-sm text-gray-500">App stability metrics (Last 30 days)</p>
        </div>

        {crashData && crashData.daily_data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {/* Crash Rate */}
              <div className={`rounded-lg p-4 border ${
                crashData.overall_crash_rate < 0.1 ? 'bg-green-50 border-green-200' :
                crashData.overall_crash_rate < 1 ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <p className="text-sm font-medium mb-2">Crash Rate</p>
                <p className={`text-3xl font-bold ${
                  crashData.overall_crash_rate < 0.1 ? 'text-green-600' :
                  crashData.overall_crash_rate < 1 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {crashData.overall_crash_rate}%
                </p>
                <p className={`text-xs mt-1 ${
                  crashData.overall_crash_rate < 0.1 ? 'text-green-600' :
                  crashData.overall_crash_rate < 1 ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {crashData.overall_crash_rate < 0.1 ? '✓ Excellent' :
                   crashData.overall_crash_rate < 1 ? '⚠ Monitor' :
                   '🚨 Critical'}
                </p>
              </div>

              {/* Crash-Free Sessions */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 font-medium mb-2">Crash-Free Sessions</p>
                <p className="text-3xl font-bold text-blue-600">{crashData.crash_free_sessions_rate}%</p>
                <p className="text-xs text-blue-600 mt-1">Target: &gt;99.9%</p>
              </div>

              {/* Total Crashes */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                <p className="text-sm text-red-700 font-medium mb-2">Total Crashes</p>
                <p className="text-3xl font-bold text-red-600">{crashData.total_crashes}</p>
                <p className="text-xs text-red-600 mt-1">Last 30 days</p>
              </div>

              {/* Total Sessions */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-700 font-medium mb-2">Total Sessions</p>
                <p className="text-3xl font-bold text-gray-600">{crashData.total_sessions.toLocaleString()}</p>
                <p className="text-xs text-gray-600 mt-1">Last 30 days</p>
              </div>
            </div>

            {/* Trend Chart */}
            <div className="border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-gray-900 mb-4">Crash Trend (30-day)</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={crashData.daily_data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
                  <YAxis yAxisId="left" stroke="#6b7280" style={{ fontSize: '12px' }}>
                    <Label value="Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                  <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" style={{ fontSize: '12px' }}>
                    <Label value="Rate %" angle={90} position="insideRight" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="crashes"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={{ fill: '#ef4444', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Crashes"
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="sessions"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Sessions"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="crash_rate"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#8b5cf6', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Crash Rate %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Top Crash Reasons */}
            {crashData.top_crash_reasons && crashData.top_crash_reasons.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Top Crash Reasons</h4>
                <div className="space-y-3">
                  {crashData.top_crash_reasons.map((crash, index) => (
                    <div
                      key={index}
                      className="p-4 bg-red-50 border border-red-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
                              #{index + 1}
                            </span>
                            <span className="text-xs text-red-700 font-medium">
                              {crash.error_name || 'Error'}
                            </span>
                          </div>
                          <p className="font-mono text-sm text-gray-900 break-words">
                            {crash.error_message}
                          </p>
                        </div>
                        <div className="ml-4 text-right">
                          <p className="text-2xl font-bold text-red-600">{crash.count}</p>
                          <p className="text-xs text-red-600">occurrences</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-600 mt-2 pt-2 border-t border-red-200">
                        <span>Versions: {crash.affected_versions.join(', ') || 'Unknown'}</span>
                        <span>Last: {new Date(crash.last_occurrence).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Insights */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>📊</span> Insights
              </h4>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>
                  • Crash rate: <strong>{crashData.overall_crash_rate}%</strong>
                  {crashData.overall_crash_rate < 0.1 && <span className="text-green-600 ml-2">✓ Excellent stability!</span>}
                  {crashData.overall_crash_rate >= 0.1 && crashData.overall_crash_rate < 1 && <span className="text-yellow-600 ml-2">⚠ Monitor closely</span>}
                  {crashData.overall_crash_rate >= 1 && <span className="text-red-600 ml-2">🚨 Critical - immediate action needed</span>}
                </li>
                <li>
                  • <strong>{crashData.crash_free_sessions_rate}%</strong> of sessions are crash-free
                  {crashData.crash_free_sessions_rate >= 99.9 && <span className="text-green-600 ml-2">(Target met)</span>}
                </li>
                <li>• Average: <strong>{(crashData.total_crashes / 30).toFixed(1)}</strong> crashes per day</li>
                {crashData.top_crash_reasons && crashData.top_crash_reasons.length > 0 && (
                  <li className="text-red-600">
                    • Top issue: <strong>{crashData.top_crash_reasons[0].error_message.substring(0, 60)}...</strong>
                    ({crashData.top_crash_reasons[0].count} occurrences)
                  </li>
                )}
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className="h-[350px] flex items-center justify-center bg-green-50 rounded-lg border-2 border-green-300">
              <div className="text-center p-6">
                <svg className="w-16 h-16 text-green-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-900 font-semibold mb-2">No Crashes Recorded</p>
                <p className="text-sm text-green-800">Excellent app stability - no crashes in the last 30 days!</p>
                <p className="text-xs text-green-700 mt-2">Data will appear here if crashes occur</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this will show:</strong> Percentage of app sessions that experienced a crash, measured daily.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Target is below 0.1%. Anything above 1% is critical. Use to identify problematic versions and prioritize stability fixes.</p>
            </div>
          </>
        )}
      </div>

      {/* Chart 3: API Response Times */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">API Response Times</h3>
          <p className="text-sm text-gray-500">Top 20 endpoints by request volume (Last 30 days)</p>
        </div>

        {apiData && apiData.endpoint_stats && apiData.endpoint_stats.length > 0 ? (
          <>
            {/* Overall Metrics */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 font-medium mb-1">Average Response Time</p>
                <p className="text-3xl font-bold text-blue-600">{apiData.overall_avg_ms}ms</p>
                <p className="text-xs text-blue-600 mt-1">
                  {apiData.overall_avg_ms < 500 ? '✓ Target met (<500ms)' : '⚠ Above target'}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-purple-700 font-medium mb-1">P95 Response Time</p>
                <p className="text-3xl font-bold text-purple-600">{apiData.overall_p95_ms}ms</p>
                <p className="text-xs text-purple-600 mt-1">95% of requests faster</p>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 border border-indigo-200">
                <p className="text-sm text-indigo-700 font-medium mb-1">P99 Response Time</p>
                <p className="text-3xl font-bold text-indigo-600">{apiData.overall_p99_ms}ms</p>
                <p className="text-xs text-indigo-600 mt-1">99% of requests faster</p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <p className="text-sm text-gray-700 font-medium mb-1">Requests Tracked</p>
                <p className="text-3xl font-bold text-gray-600">{apiData.total_requests_tracked.toLocaleString()}</p>
                <p className="text-xs text-gray-600 mt-1">10% sampled</p>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <ResponsiveContainer width="100%" height={450}>
                <BarChart
                  data={apiData.endpoint_stats}
                  layout="vertical"
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    type="number"
                    stroke="#6b7280"
                  >
                    <Label value="Response Time (ms)" position="insideBottom" offset={-15} />
                  </XAxis>
                  <YAxis
                    type="category"
                    dataKey="endpoint"
                    stroke="#6b7280"
                    width={120}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'Average') return [`${value}ms`, name];
                      if (name === 'P95') return [`${value}ms`, name];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '25px' }} />
                  <Bar
                    dataKey="avg_duration_ms"
                    name="Average"
                    radius={[0, 4, 4, 0]}
                  >
                    {apiData.endpoint_stats.map((entry, index) => {
                      const color = entry.avg_duration_ms < 200 ? '#10b981' :
                                   entry.avg_duration_ms < 500 ? '#3b82f6' :
                                   entry.avg_duration_ms < 1000 ? '#f59e0b' : '#ef4444';
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                  <Bar
                    dataKey="p95_duration_ms"
                    name="P95"
                    fill="#8b5cf6"
                    radius={[0, 4, 4, 0]}
                    opacity={0.6}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Performance Legend */}
            <div className="flex flex-wrap gap-4 text-sm mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-500"></div>
                <span>Fast (&lt;200ms)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500"></div>
                <span>Good (200-500ms)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-500"></div>
                <span>Slow (500-1000ms)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>Very Slow (&gt;1000ms)</span>
              </div>
            </div>

            {/* Insights */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-2">📊 Performance Insights</h4>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>
                  • Overall average response time: <strong>{apiData.overall_avg_ms}ms</strong>
                  {apiData.overall_avg_ms < 500 && <span className="text-green-600 ml-2">✓ Meets target</span>}
                  {apiData.overall_avg_ms >= 500 && <span className="text-red-600 ml-2">⚠ Above 500ms target</span>}
                </li>
                <li>• 95% of requests complete in under <strong>{apiData.overall_p95_ms}ms</strong></li>
                <li>• Tracked {apiData.total_requests_tracked.toLocaleString()} API calls ({apiData.sampling_rate})</li>
                {apiData.endpoint_stats.filter(e => e.avg_duration_ms > 1000).length > 0 && (
                  <li className="text-red-600">
                    • ⚠ {apiData.endpoint_stats.filter(e => e.avg_duration_ms > 1000).length} endpoints averaging &gt;1000ms - investigate optimization
                  </li>
                )}
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className="h-[350px] flex items-center justify-center bg-amber-50 rounded-lg border-2 border-amber-300">
              <div className="text-center p-6">
                <svg className="w-16 h-16 text-amber-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-amber-900 font-semibold mb-2">No API Performance Data Available</p>
                <p className="text-sm text-amber-800">No API calls have been tracked yet</p>
                <p className="text-xs text-amber-700 mt-2">Data will appear once mobile app starts tracking API performance</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this will show:</strong> Average API response times for common endpoints showing backend performance from mobile perspective.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Target is below 500ms for good UX. Above 1000ms is poor. Slow endpoints need optimization.</p>
            </div>
          </>
        )}
      </div>

      {/* Chart 4: Push Notification Open Rate */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Push Notification Open Rate</h3>
          <p className="text-sm text-gray-500">Last 30 days</p>
        </div>

        {notificationData && notificationData.daily_data && notificationData.daily_data.length > 0 ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-purple-700 font-medium mb-1">Overall Open Rate</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-purple-600">{notificationData.overall_open_rate}%</p>
                  {notificationData.overall_open_rate >= 20 && (
                    <span className="text-xs text-green-600">✓ Excellent!</span>
                  )}
                  {notificationData.overall_open_rate >= 10 && notificationData.overall_open_rate < 20 && (
                    <span className="text-xs text-yellow-600">⚠ Good</span>
                  )}
                  {notificationData.overall_open_rate < 10 && (
                    <span className="text-xs text-red-600">⚠ Low</span>
                  )}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-blue-700 font-medium mb-1">Total Sent</p>
                <p className="text-3xl font-bold text-blue-600">{notificationData.total_sent.toLocaleString()}</p>
                <p className="text-xs text-blue-600 mt-1">Avg: {(notificationData.total_sent / 30).toFixed(1)}/day</p>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-green-700 font-medium mb-1">Total Opened</p>
                <p className="text-3xl font-bold text-green-600">{notificationData.total_opened.toLocaleString()}</p>
                <p className="text-xs text-green-600 mt-1">Avg: {(notificationData.total_opened / 30).toFixed(1)}/day</p>
              </div>
            </div>

            {/* Line Chart */}
            <div className="border border-gray-200 rounded-lg p-4">
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={notificationData.daily_data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  >
                    <Label value="Count" angle={-90} position="insideLeft" style={{ textAnchor: 'middle' }} />
                  </YAxis>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value, name) => {
                      if (name === 'Open Rate') return [`${value}%`, name];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Line
                    type="monotone"
                    dataKey="sent"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Sent"
                  />
                  <Line
                    type="monotone"
                    dataKey="opened"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Opened"
                  />
                  <Line
                    type="monotone"
                    dataKey="open_rate"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#8b5cf6', r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Open Rate (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Insights */}
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <span>📊</span> Key Insights
              </h4>
              <div className="space-y-1 text-sm text-gray-700">
                <p>
                  • <strong>{notificationData.overall_open_rate}%</strong> of users open push notifications
                  {notificationData.overall_open_rate >= 20 && <span className="text-green-600 ml-2">✓ Excellent engagement!</span>}
                  {notificationData.overall_open_rate >= 10 && notificationData.overall_open_rate < 20 && <span className="text-yellow-600 ml-2">⚠ Good engagement</span>}
                  {notificationData.overall_open_rate < 10 && <span className="text-red-600 ml-2">⚠ Needs improvement</span>}
                </p>
                <p>• Daily average: <strong>{(notificationData.total_sent / 30).toFixed(1)}</strong> sent, <strong>{(notificationData.total_opened / 30).toFixed(1)}</strong> opened</p>
                {notificationData.overall_open_rate < 10 && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-xs">
                    💡 <strong>Tip:</strong> Consider optimizing notification content, timing, or frequency to improve engagement
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="h-[350px] flex items-center justify-center bg-amber-50 rounded-lg border-2 border-amber-300">
              <div className="text-center p-6">
                <svg className="w-16 h-16 text-amber-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="text-amber-900 font-semibold mb-2">No Notification Data Available</p>
                <p className="text-sm text-amber-800">No notifications have been sent yet, or tracking is still being implemented</p>
                <p className="text-xs text-amber-700 mt-2">Data will appear once mobile users start tapping notifications</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this will show:</strong> Percentage of push notifications that users actually open/tap.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Above 20% is excellent. 10-20% is good. Below 10% means notifications need optimization.</p>
            </div>
          </>
        )}
      </div>

      {/* Chart 5: App Version Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">App Version Distribution</h3>
        {versionData && versionData.version_distribution && versionData.version_distribution.length > 0 ? (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <p className="text-sm text-blue-700 font-medium">Total Active Users</p>
                <p className="text-3xl font-bold text-blue-900">{versionData.total_active_users}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium">Latest Version</p>
                <p className="text-3xl font-bold text-green-900">{versionData.latest_version}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <p className="text-sm text-purple-700 font-medium">Versions in Use</p>
                <p className="text-3xl font-bold text-purple-900">{versionData.version_distribution.length}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={versionData.version_distribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ version, percentage }) => `v${version}: ${percentage}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="user_count"
                >
                  {versionData.version_distribution.map((entry, index) => {
                    const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6b7280'];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} users (${props.payload.percentage}%)`,
                    `Version ${props.payload.version}`
                  ]}
                />
                <Legend 
                  formatter={(value, entry) => `v${entry.payload.version}`}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this shows:</strong> Breakdown of users by app version showing update adoption rate.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Most users on latest version is ideal. Many on old versions suggests update friction or users ignoring updates. If critical bug exists in old version, may need forced update.</p>
            </div>
          </>
        ) : (
          <>
            <div className="h-[350px] flex items-center justify-center bg-amber-50 rounded-lg border-2 border-amber-300">
              <div className="text-center p-6">
                <svg className="w-16 h-16 text-amber-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-amber-900 font-semibold mb-2">No Version Data Available</p>
                <p className="text-sm text-amber-800">No users have logged in with app version tracking yet</p>
                <p className="text-xs text-amber-700 mt-2">Data will appear once users login with the updated mobile app</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700"><strong>What this will show:</strong> Breakdown of users by app version showing update adoption rate.</p>
              <p className="text-sm text-gray-700 mt-2"><strong>How to interpret:</strong> Most users on latest version is ideal. Many on old versions suggests update friction or users ignoring updates.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceTechnicalPage;